/* =====================================================================
   Intervention schema — the atom strategies are made of.
   Byte-compatible with the prototype (T-WS/APP/js/interventions.js):
   every intervention INSTANCE carries its own impact profile; the
   preset library is a set of calibrated starting points, not a ceiling.
   ===================================================================== */
import { z } from 'zod';
import { zLevers, type Levers, copyLevers } from './levers.js';

export const IV_SIZES = ['S', 'M', 'L', 'XL'] as const;
export const IV_OPEXES = ['saves', 'neutral', 'costs'] as const;
export const IV_SPEEDS = ['quick', 'fast', 'medium', 'slow'] as const;
export const IV_TAG_KEYS = ['regen', 'services', 'land'] as const;
export const PRIORITIES = ['High', 'Medium', 'Low'] as const;

export type IvSize = (typeof IV_SIZES)[number];
export type IvOpex = (typeof IV_OPEXES)[number];
export type IvSpeed = (typeof IV_SPEEDS)[number];
export type IvTag = (typeof IV_TAG_KEYS)[number];
export type Priority = (typeof PRIORITIES)[number];

/** Engine mappings — the numbers the micro-simulation consumes. */
export const IV_SIZE: Record<IvSize, { label: string; capex: number }> = {
  S:  { label: 'Small',      capex: 0.05 },   // capex as fraction of annual baseline revenue
  M:  { label: 'Medium',     capex: 0.15 },
  L:  { label: 'Large',      capex: 0.30 },
  XL: { label: 'Very large', capex: 0.50 },
};
export const IV_OPEX: Record<IvOpex, { label: string; d: number }> = {
  saves:   { label: 'Saves money', d: -0.02 }, // delta annual operating cost, fraction of revenue
  neutral: { label: '~ Neutral',   d: 0 },
  costs:   { label: 'Costs money', d: +0.02 },
};
export const IV_SPEED: Record<IvSpeed, { label: string; tau: number }> = {
  quick:  { label: 'Quick (<1y)', tau: 0.5 },  // ramp-in time constant, years
  fast:   { label: '1–3 years',   tau: 2 },
  medium: { label: '3–7 years',   tau: 5 },
  slow:   { label: '7+ years',    tau: 10 },
};
export const IV_TAGS: Record<IvTag, string> = {
  regen: 'Regenerative / transition',
  services: 'Shifts value to services',
  land: 'Land & soils',
};
/** Priority -> implementation intensity. */
export const PRIORITY_INTENSITY: Record<Priority, number> = { High: 1.0, Medium: 0.65, Low: 0.35 };

/** The persistent impact profile of one intervention instance. */
export const zInterventionProfile = z.object({
  iid: z.string(),
  ref: z.string().nullable(),          // provenance: preset key, or null = fully custom
  custom: z.boolean(),
  name: z.string(),
  desc: z.string().default(''),
  owner: z.string().default(''),
  priority: z.enum(PRIORITIES),
  levers: zLevers,
  size: z.enum(IV_SIZES),
  opex: z.enum(IV_OPEXES),
  speed: z.enum(IV_SPEEDS),
  tags: z.array(z.enum(IV_TAG_KEYS)).default([]),
});
export type InterventionProfile = z.infer<typeof zInterventionProfile>;

/** A preset — same shape minus instance fields. */
export interface InterventionPreset {
  name: string;
  owner: string;
  levers: Partial<Levers>;
  size: IvSize;
  opex: IvOpex;
  speed: IvSpeed;
  tags: IvTag[];
}

/** The built-in library: calibrated presets (identical to the prototype). */
export const IV_PRESETS: Record<string, InterventionPreset> = {
  cash_buffer:    { name: 'Strategic reserves & cash buffer', owner: 'CFO',             levers: { resilience: .92, diversification: .22 },                    size: 'M',  opex: 'costs',   speed: 'quick',  tags: [] },
  local_sourcing: { name: 'Localize critical sourcing',       owner: 'Head of Supply',  levers: { resilience: .78, diversification: .74 },                    size: 'M',  opex: 'costs',   speed: 'fast',   tags: [] },
  circular:       { name: 'Close material loops',             owner: 'Head of Ops',     levers: { efficiency: .94, diversification: .34 },                    size: 'L',  opex: 'saves',   speed: 'medium', tags: ['regen'] },
  onsite_energy:  { name: 'On-site renewable energy',         owner: 'Facilities',      levers: { efficiency: .72, resilience: .54 },                         size: 'L',  opex: 'saves',   speed: 'fast',   tags: ['regen'] },
  hedge:          { name: 'Hedge commodity exposure',         owner: 'Treasury',        levers: { resilience: .72, diversification: .6 },                     size: 'S',  opex: 'costs',   speed: 'quick',  tags: [] },
  automation:     { name: 'Automation & productivity',        owner: 'CTO',             levers: { efficiency: .82, growth: .44 },                             size: 'L',  opex: 'saves',   speed: 'fast',   tags: [] },
  expand_markets: { name: 'Aggressive market expansion',      owner: 'CRO',             levers: { growth: .95, diversification: .2 },                         size: 'L',  opex: 'costs',   speed: 'fast',   tags: [] },
  scale_capex:    { name: 'Scale up capacity (capex)',        owner: 'COO',             levers: { growth: .88 },                                              size: 'XL', opex: 'neutral', speed: 'medium', tags: [] },
  new_geo:        { name: 'Enter new geographies',            owner: 'Head of Growth',  levers: { growth: .7, diversification: .58 },                         size: 'L',  opex: 'costs',   speed: 'medium', tags: [] },
  regen_rnd:      { name: 'R&D into regenerative inputs',     owner: 'Chief Scientist', levers: { efficiency: .6, diversification: .54, growth: .34 },        size: 'M',  opex: 'costs',   speed: 'slow',   tags: ['regen'] },
  reskill:        { name: 'Reskill & retain workforce',       owner: 'CHRO',            levers: { resilience: .62, diversification: .46 },                    size: 'M',  opex: 'costs',   speed: 'fast',   tags: [] },
  deleverage:     { name: 'Deleverage balance sheet',         owner: 'CFO',             levers: { resilience: .76 },                                          size: 'M',  opex: 'saves',   speed: 'fast',   tags: [] },
  soil_restore:   { name: 'Restore land & soil assets',       owner: 'Sustainability',  levers: { efficiency: .5, resilience: .6, diversification: .42 },     size: 'L',  opex: 'costs',   speed: 'slow',   tags: ['regen', 'land'] },
  demand_shift:   { name: 'Shift value to services',          owner: 'Strategy',        levers: { efficiency: .66, growth: .32 },                             size: 'M',  opex: 'neutral', speed: 'medium', tags: ['services'] },
};

/** Full instance from a library preset. */
export function instantiatePreset(
  ref: string,
  extra: { iid: string; priority: Priority } & Partial<InterventionProfile>,
): InterventionProfile {
  const p = IV_PRESETS[ref];
  if (!p) throw new Error(`instantiatePreset: unknown preset "${ref}"`);
  return {
    ref, custom: false, name: p.name, desc: '', owner: p.owner,
    levers: copyLevers(p.levers), size: p.size, opex: p.opex, speed: p.speed, tags: [...p.tags],
    ...extra,
  };
}

/** Hydrate/migrate any stored intervention to the full schema (idempotent). */
export function hydrateIntervention(iv: Partial<InterventionProfile> & { iid: string }): InterventionProfile {
  const p = iv.ref ? IV_PRESETS[iv.ref] : undefined;
  return {
    iid: iv.iid,
    ref: iv.ref ?? null,
    custom: iv.custom ?? !p,
    name: iv.name ?? p?.name ?? 'Untitled intervention',
    desc: iv.desc ?? '',
    owner: iv.owner ?? p?.owner ?? '',
    priority: iv.priority ?? 'Medium',
    levers: iv.levers ? copyLevers(iv.levers)
      : p ? copyLevers(p.levers)
      : { efficiency: .25, resilience: .25, growth: .25, diversification: .25 },
    size: iv.size ?? p?.size ?? 'M',
    opex: iv.opex ?? p?.opex ?? 'neutral',
    speed: iv.speed ?? p?.speed ?? 'fast',
    tags: iv.tags ?? (p ? [...p.tags] : []),
  };
}
